import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function SmartContractHacks() {
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitData, setSubmitData] = useState({
    projectName: '',
    description: '',
    month: '',
    year: '',
    amount: '',
    submitterEmail: '',
    submitterName: '',
    markdownContent: '',
    images: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);

  const [hacks, setHacks] = useState([]);
  const [loadingHacks, setLoadingHacks] = useState(true);

  // Fetch approved hacks from database
  useEffect(() => {
    const fetchHacks = async () => {
      try {
        const response = await fetch('/api/get-public-submissions?type=hack');
        const result = await response.json();
        
        if (result.success) {
          setHacks(result.submissions);
        } else {
          console.error('Failed to fetch hacks:', result.error);
        }
      } catch (error) {
        console.error('Error fetching hacks:', error);
      } finally {
        setLoadingHacks(false);
      }
    };

    fetchHacks();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSubmitData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB limit
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    for (const file of files) {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }

      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} is not a supported image type.`);
        continue;
      }

      // Convert to base64 for email attachment
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        const imageData = {
          name: file.name,
          size: file.size,
          type: file.type,
          base64: base64
        };
        
        setUploadedImages(prev => [...prev, imageData]);
        setSubmitData(prev => ({
          ...prev,
          images: [...prev.images, imageData]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setSubmitData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('/api/submit-hack', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectName: submitData.projectName,
          description: submitData.description,
          month: submitData.month,
          year: submitData.year,
          amount: submitData.amount,
          submitterName: submitData.submitterName,
          submitterEmail: submitData.submitterEmail,
          markdownContent: submitData.markdownContent,
          images: uploadedImages
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitData({
          projectName: '',
          description: '',
          month: '',
          year: '',
          amount: '',
          submitterEmail: '',
          submitterName: '',
          markdownContent: '',
          images: []
        });
        setUploadedImages([]);
        setTimeout(() => {
          setShowSubmitForm(false);
          setSubmitStatus('');
        }, 3000);
      } else {
        setSubmitStatus('error');
        console.error('Submission failed:', result.error);
      }
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="hacks-page">
      <div className="container">
        <div className="page-header">
          <h1>Smart Contract Hacks Database</h1>
          <p className="page-subtitle">
            A comprehensive list of known smart contract exploits and security incidents.
          </p>
          <button 
            className="cta-button" 
            onClick={() => setShowSubmitForm(!showSubmitForm)}
          >
            {showSubmitForm ? 'Cancel' : 'Submit New Hack'}
          </button>
        </div>

        {showSubmitForm && (
          <div className="submit-form-container">
            <h2>Submit New Hack</h2>
            <form onSubmit={handleSubmit} className="submit-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="projectName">Project Name *</label>
                  <input
                    type="text"
                    id="projectName"
                    name="projectName"
                    value={submitData.projectName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="amount">Amount Lost</label>
                  <input
                    type="text"
                    id="amount"
                    name="amount"
                    value={submitData.amount}
                    onChange={handleInputChange}
                    placeholder="e.g., $100M"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="month">Month *</label>
                  <select
                    id="month"
                    name="month"
                    value={submitData.month}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="year">Year *</label>
                  <input
                    type="number"
                    id="year"
                    name="year"
                    value={submitData.year}
                    onChange={handleInputChange}
                    min="2009"
                    max={new Date().getFullYear()}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Brief Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={submitData.description}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Brief description of the hack/exploit..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="markdownContent">Full Report (Markdown)</label>
                <textarea
                  id="markdownContent"
                  name="markdownContent"
                  value={submitData.markdownContent}
                  onChange={handleInputChange}
                  rows="10"
                  placeholder={`# Detailed Analysis

## Overview
Describe the hack in detail...

## Technical Details
Explain the technical aspects...

## Impact
What was the impact?

## Lessons Learned
What can we learn from this?

## References
- Link 1
- Link 2`}
                />
                <small className="form-help">
                  You can use full Markdown syntax including headers, lists, links, code blocks, etc.
                </small>
              </div>

              {submitData.markdownContent && (
                <div className="form-group">
                  <label>Preview</label>
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {submitData.markdownContent}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="images">Upload Images (Optional)</label>
                <input
                  type="file"
                  id="images"
                  name="images"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <small className="form-help">
                  Maximum 5MB per image. Supported formats: JPEG, PNG, GIF, WebP
                </small>
                
                {uploadedImages.length > 0 && (
                  <div className="uploaded-images">
                    <h4>Uploaded Images:</h4>
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="image-preview">
                        <img src={image.base64} alt={image.name} />
                        <div className="image-info">
                          <span>{image.name}</span>
                          <span>{(image.size / 1024 / 1024).toFixed(2)}MB</span>
                          <button 
                            type="button" 
                            onClick={() => removeImage(index)}
                            className="remove-image"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="submitterName">Your Name *</label>
                  <input
                    type="text"
                    id="submitterName"
                    name="submitterName"
                    value={submitData.submitterName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="submitterEmail">Your Email *</label>
                  <input
                    type="email"
                    id="submitterEmail"
                    name="submitterEmail"
                    value={submitData.submitterEmail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="form-status success">
                  ✅ Thank you! Your submission has been sent for review.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="form-status error">
                  ❌ There was an error. Please try again.
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="cta-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="hacks-table-container">
          {loadingHacks ? (
            <div className="loading-state">
              <p>Loading hacks...</p>
            </div>
          ) : hacks.length === 0 ? (
            <div className="empty-state">
              <p>No approved hacks found. Be the first to submit one!</p>
            </div>
          ) : (
            <table className="hacks-table">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Description</th>
                  <th>Month</th>
                  <th>Year</th>
                  <th>Amount</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {hacks.map((hack) => (
                  <tr key={hack.id}>
                    <td className="project-name">{hack.projectName}</td>
                    <td className="description">{hack.description}</td>
                    <td className="month">{hack.month}</td>
                    <td className="year">{hack.year}</td>
                    <td className="amount">{hack.amount}</td>
                    <td className="details">
                      <a href={`/hack-details/${hack.id}`} className="details-link">
                        {hack.hasFullReport ? '📄 Full Report' : 'View Details'}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default SmartContractHacks;
